// Browser-side image preparation shared by artwork uploads and exhibition photos.

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
export const IMAGE_ACCEPT = 'image/*,.heic,.heif,.jpg,.jpeg,.png,.gif,.webp';

export const isHeicFile = (file) => {
  const name = file?.name || '';
  const type = file?.type || '';
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    /\.(heic|heif)$/i.test(name)
  );
};

// Older browsers cannot display HEIC, so convert it before preview/upload.
export const convertHeicToJpeg = async (file) => {
  try {
    const heic2any = (await import('heic2any')).default;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
  } catch (conversionError) {
    console.warn('HEIC→JPEG conversion failed, using original file:', conversionError);
    return file;
  }
};

const readImageFromFile = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = (e) => {
    URL.revokeObjectURL(url);
    reject(e);
  };
  img.src = url;
});

const canvasToFile = (canvas, type, quality, filename) => new Promise((resolve) => {
  canvas.toBlob((blob) => {
    resolve(new File([blob], filename, { type }));
  }, type, quality);
});

export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 2400,
    maxHeight = 2400,
    initialQuality = 0.88,
    minQuality = 0.6,
    maxBytes = 10 * 1024 * 1024 * 0.95
  } = options;

  const img = await readImageFromFile(file);

  const { width, height } = img;
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const baseName = file.name.replace(/\.[^.]+$/, '');
  let quality = initialQuality;
  let output = await canvasToFile(canvas, 'image/jpeg', quality, `${baseName}.jpg`);

  while (output.size > maxBytes && quality > minQuality) {
    quality = Math.max(minQuality, quality - 0.08);
    output = await canvasToFile(canvas, 'image/jpeg', quality, `${baseName}.jpg`);
  }

  return output;
};

/**
 * Convert, compress, and filter files before upload.
 * Returns the files that are ready and the ones that were rejected with a reason.
 */
export const prepareImageFiles = async (files, { maxBytes, maxCount = Infinity }) => {
  const ready = [];
  const rejected = [];
  const maxMB = Math.round(maxBytes / (1024 * 1024));

  for (const original of files) {
    if (ready.length >= maxCount) {
      rejected.push({ name: original.name, reason: 'over the photo limit' });
      continue;
    }

    let file = isHeicFile(original) ? await convertHeicToJpeg(original) : original;

    if (!file.type.startsWith('image/')) {
      rejected.push({ name: file.name, reason: 'not an image' });
      continue;
    }

    if (file.size > maxBytes * 0.8) {
      try {
        const compressed = await compressImage(file, { maxBytes: maxBytes * 0.95 });
        if (compressed.size < file.size) file = compressed;
      } catch (compressionError) {
        console.warn('Compression failed for', file.name, compressionError);
      }
    }

    if (file.size > maxBytes) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
      rejected.push({ name: file.name, reason: `too large (${sizeMb}MB) - max ${maxMB}MB` });
      continue;
    }

    ready.push(file);
  }

  return { ready, rejected };
};
